import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Buildings, Headset, Question, UploadSimple, Trash, Envelope, CheckCircle, Layout, Tray, PaperPlaneTilt, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import BriefkopfDesigner from "@/components/BriefkopfDesigner";
import { Switch } from "@/components/ui/switch";

const SECTIONS = [
  { key: "profil", label: "Profil", icon: User },
  { key: "kanzlei", label: "Kanzleidaten", icon: Buildings },
  { key: "briefkopf", label: "Briefkopf & Fußzeile", icon: Layout },
  { key: "email", label: "E-Mail (IMAP & SMTP)", icon: PaperPlaneTilt },
  { key: "portal", label: "Mandantenportal", icon: DeviceMobile },
  { key: "support", label: "Support", icon: Headset },
  { key: "hilfe", label: "Hilfe & FAQ", icon: Question },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get("section") || "profil");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    const s = searchParams.get("section");
    if (s && s !== active) setActive(s);
    // eslint-disable-next-line
  }, [searchParams]);

  const changeActive = (key) => {
    setActive(key);
    setSearchParams({ section: key });
  };

  const loadAvatar = async () => {
    if (!user?.avatar_path) { setAvatarUrl(null); return; }
    try {
      const res = await api.get("/auth/avatar", { responseType: "blob" });
      setAvatarUrl(URL.createObjectURL(res.data));
    } catch { setAvatarUrl(null); }
  };

  useEffect(() => { loadAvatar(); /* eslint-disable-next-line */ }, [user?.avatar_path]);

  const uploadAvatar = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/auth/avatar", fd);
      const updated = { ...user, avatar_path: r.data.avatar_path };
      localStorage.setItem("kvr_user", JSON.stringify(updated));
      setUser(updated);
      toast.success("Profilbild aktualisiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const deleteAvatar = async () => {
    await api.delete("/auth/avatar");
    const updated = { ...user, avatar_path: null };
    localStorage.setItem("kvr_user", JSON.stringify(updated));
    setUser(updated);
    setAvatarUrl(null);
    toast.success("Profilbild entfernt");
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <header>
        <p className="overline text-[#D32F2F] mb-2">Einstellungen</p>
        <h1 className="font-serif text-4xl tracking-tight">Konto & Kanzlei</h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="lg:sticky lg:top-8 lg:self-start">
          <nav className="space-y-1">
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => changeActive(key)}
                data-testid={`settings-nav-${key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  active === key ? "bg-[#1A2238] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
                }`}
              >
                <Icon size={18} weight="duotone" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">
          {active === "profil" && (
            <div className="space-y-6">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-8">
                <h3 className="font-serif text-2xl mb-6">Profilbild</h3>
                <div className="flex items-center gap-6">
                  <AvatarCircle url={avatarUrl} name={user?.full_name} size={96} />
                  <div className="flex gap-2">
                    <input type="file" accept="image/*" ref={fileRef} className="hidden" data-testid="avatar-input" onChange={e => { const f = e.target.files?.[0]; if (f) uploadAvatar(f); }} />
                    <Button onClick={() => fileRef.current?.click()} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="avatar-upload-btn">
                      <UploadSimple size={16} className="mr-2" /> Foto hochladen
                    </Button>
                    {user?.avatar_path && <Button variant="outline" onClick={deleteAvatar} data-testid="avatar-delete-btn"><Trash size={16} /></Button>}
                  </div>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-4">PNG / JPG, max. 5 MB. Wird oben rechts im Menü angezeigt.</p>
              </div>

              <ProfileForm user={user} setUser={setUser} />
            </div>
          )}

          {active === "kanzlei" && <KanzleiForm user={user} setUser={setUser} />}

          {active === "briefkopf" && <BriefkopfDesigner user={user} setUser={setUser} />}

          {active === "email" && <EmailServerForm user={user} setUser={setUser} />}

          {active === "portal" && <PortalSettingsForm user={user} setUser={setUser} />}

          {active === "support" && <SupportForm />}

          {active === "hilfe" && <HilfeSection />}
        </div>
      </div>
    </div>
  );
}

export function AvatarCircle({ url, name, size = 36 }) {
  const initials = (name || "?").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className="rounded-full bg-[#1A2238] text-white flex items-center justify-center font-serif overflow-hidden shrink-0 ring-1 ring-[#E5E7EB]"
      style={{ width: size, height: size, fontSize: size / 2.6 }}
      data-testid="avatar-circle"
    >
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : <span>{initials}</span>}
    </div>
  );
}

function ProfileForm({ user, setUser }) {
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    telefon: user?.telefon || "",
    anwalt_titel: user?.anwalt_titel || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", form);
      localStorage.setItem("kvr_user", JSON.stringify(r.data));
      setUser(r.data);
      toast.success("Profil aktualisiert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-[#E5E7EB] rounded-lg p-8 space-y-4" data-testid="profile-form">
      <h3 className="font-serif text-2xl mb-2">Persönliche Daten</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Vollständiger Name</Label>
          <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} data-testid="profile-fullname" />
        </div>
        <div>
          <Label className="overline">Anwaltstitel / Spezialisierung</Label>
          <Input value={form.anwalt_titel} onChange={e => setForm(f => ({ ...f, anwalt_titel: e.target.value }))} placeholder="z.B. Fachanwalt für Verkehrsrecht" data-testid="profile-anwalttitel" />
        </div>
        <div>
          <Label className="overline">E-Mail (nicht änderbar)</Label>
          <Input value={user?.email || ""} disabled />
        </div>
        <div>
          <Label className="overline">Telefon</Label>
          <Input value={form.telefon} onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))} data-testid="profile-telefon" />
        </div>
      </div>
      <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="profile-save">
        {saving ? "Speichert..." : "Speichern"}
      </Button>
    </form>
  );
}

// Modul-Ebene (nicht im Render!) — sonst wird das Input bei jedem Tastendruck neu erzeugt und verliert den Fokus
function KanzleiField({ form, set, k, label, placeholder, cols }) {
  return (
    <div className={cols === 2 ? "md:col-span-2" : ""}>
      <Label className="overline">{label}</Label>
      <Input value={form[k] ?? ""} onChange={e => set(k, e.target.value)} placeholder={placeholder} data-testid={`kanzlei-${k}`} />
    </div>
  );
}

const AZ_PRESETS = [
  ["{Nr4}/{JJ} {Kürzel}", "0061/26 YY"],
  ["{Nr4}/{JJ}", "0061/26"],
  ["{Nr}/{JJ} {Kürzel}", "61/26 YY"],
  ["{Nr}/{JJJJ}", "61/2026"],
  ["{JJJJ}-{Nr4}", "2026-0061"],
];

function AktenzeichenSection({ form, set }) {
  const [vorschau, setVorschau] = useState("");
  const [renumbering, setRenumbering] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/akten/aktenzeichen-vorschau", { params: { fmt: form.az_format, kuerzel: form.anwalt_kuerzel } });
        setVorschau(r.data.vorschau);
      } catch { setVorschau(""); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.az_format, form.anwalt_kuerzel]);

  const renumber = async () => {
    if (!window.confirm("Alle bestehenden Aktenzeichen auf das gespeicherte Format umstellen? Nummer und Jahr bleiben erhalten.")) return;
    setRenumbering(true);
    try {
      const r = await api.post("/akten/umnummerieren");
      toast.success(`${r.data.geaendert} von ${r.data.gesamt} Akten umnummeriert`);
    } catch (err) { toast.error(errMsg(err, "Umnummerierung fehlgeschlagen")); } finally { setRenumbering(false); }
  };

  return (
    <div data-testid="aktenzeichen-section">
      <p className="overline text-[#9CA3AF] mb-3">Aktenzeichen</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="overline">Anwaltskürzel</Label>
          <Input value={form.anwalt_kuerzel ?? ""} onChange={e => set("anwalt_kuerzel", e.target.value.toUpperCase().slice(0, 6))} placeholder="z. B. YY" data-testid="kanzlei-anwalt_kuerzel" />
          <p className="text-[11px] text-[#9CA3AF] mt-1">Leer = Initialen aus dem Namen</p>
        </div>
        <div className="md:col-span-2">
          <Label className="overline">Format</Label>
          <Input value={form.az_format ?? ""} onChange={e => set("az_format", e.target.value)} placeholder="{Nr4}/{JJ} {Kürzel}" data-testid="kanzlei-az_format" />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {AZ_PRESETS.map(([fmt, ex]) => (
              <button
                key={fmt}
                type="button"
                onClick={() => set("az_format", fmt)}
                className={`px-2 py-0.5 rounded border text-[11px] font-mono transition-colors ${form.az_format === fmt ? "bg-[#1A2238] text-white border-[#1A2238]" : "border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]"}`}
                data-testid={`az-preset-${ex.replace(/[^a-z0-9]/gi, "")}`}
              >
                {ex}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-1">Platzhalter: {"{Nr}"} {"{Nr4}"} {"{JJ}"} {"{JJJJ}"} {"{Kürzel}"}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-4 py-3">
        <span className="text-xs text-[#6B7280]">Nächste Akte sieht so aus:</span>
        <span className="font-mono text-lg text-[#1A2238]" data-testid="az-vorschau">{vorschau || "…"}</span>
        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={renumber} disabled={renumbering} data-testid="az-renumber-btn">
          {renumbering ? "Läuft…" : "Bestehende Akten umnummerieren"}
        </Button>
      </div>
    </div>
  );
}

function KanzleiForm({ user, setUser }) {
  const [form, setForm] = useState({
    kanzlei_name: user?.kanzlei_name || "",
    az_format: user?.az_format || "{Nr4}/{JJ} {Kürzel}",
    anwalt_kuerzel: user?.anwalt_kuerzel || "",
    kanzlei_adresse: user?.kanzlei_adresse || "",
    kanzlei_plz: user?.kanzlei_plz || "",
    kanzlei_ort: user?.kanzlei_ort || "",
    kanzlei_telefon: user?.kanzlei_telefon || "",
    kanzlei_fax: user?.kanzlei_fax || "",
    kanzlei_email: user?.kanzlei_email || "",
    kanzlei_website: user?.kanzlei_website || "",
    kanzlei_ustid: user?.kanzlei_ustid || "",
    kanzlei_iban: user?.kanzlei_iban || "",
    kanzlei_bank: user?.kanzlei_bank || "",
    kanzlei_bic: user?.kanzlei_bic || "",
    kanzlei_anderkonto_iban: user?.kanzlei_anderkonto_iban || "",
    kanzlei_anderkonto_bic: user?.kanzlei_anderkonto_bic || "",
    kanzlei_kammer: user?.kanzlei_kammer || "",
    kanzlei_gerichtsstand: user?.kanzlei_gerichtsstand || "",
  });
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoDrag, setLogoDrag] = useState(false);
  const logoRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    let active = true; let url;
    (async () => {
      if (!user?.logo_path) { setLogoUrl(null); return; }
      try {
        const r = await api.get("/auth/logo", { responseType: "blob" });
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setLogoUrl(url);
      } catch { setLogoUrl(null); }
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [user?.logo_path]);

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/auth/logo", fd);
      const updated = { ...user, logo_path: r.data.logo_path };
      localStorage.setItem("kvr_user", JSON.stringify(updated));
      setUser(updated);
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const deleteLogo = async () => {
    await api.delete("/auth/logo");
    const updated = { ...user, logo_path: null };
    localStorage.setItem("kvr_user", JSON.stringify(updated));
    setUser(updated);
    setLogoUrl(null);
    toast.success("Logo entfernt");
  };

  const onDrop = (e) => {
    e.preventDefault();
    setLogoDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadLogo(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", form);
      localStorage.setItem("kvr_user", JSON.stringify(r.data));
      setUser(r.data);
      toast.success("Kanzleidaten gespeichert");
    } catch {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Logo Upload Card */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8" data-testid="logo-card">
        <h3 className="font-serif text-2xl mb-2">Kanzlei-Logo</h3>
        <p className="text-sm text-[#4B5563] mb-6">Erscheint im Briefkopf jedes Schriftsatzes — Größe wird automatisch angepasst.</p>
        <div
          onDragOver={(e) => { e.preventDefault(); setLogoDrag(true); }}
          onDragLeave={() => setLogoDrag(false)}
          onDrop={onDrop}
          className={`dropzone ${logoDrag ? "dropzone--active" : ""} rounded-lg p-4 md:p-8 flex flex-wrap items-center gap-4 md:gap-6`}
          data-testid="logo-dropzone"
        >
          <div className="w-32 h-20 rounded border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
            ) : (
              <span className="text-xs text-[#9CA3AF]">Kein Logo</span>
            )}
          </div>
          <div className="flex-1 min-w-[160px]">
            <p className="text-sm text-[#0A192F] mb-1">Logo hier ablegen oder Datei auswählen</p>
            <p className="text-xs text-[#9CA3AF]">PNG, JPG, SVG · max. 8 MB · transparenter Hintergrund empfohlen</p>
          </div>
          <input type="file" accept="image/*,image/svg+xml" ref={logoRef} className="hidden" data-testid="logo-input" onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); }} />
          <Button type="button" onClick={() => logoRef.current?.click()} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="logo-upload-btn">
            <UploadSimple size={16} className="mr-2" /> Auswählen
          </Button>
          {user?.logo_path && (
            <Button type="button" variant="outline" onClick={deleteLogo} data-testid="logo-delete-btn"><Trash size={16} /></Button>
          )}
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-[#E5E7EB] rounded-lg p-8 space-y-6" data-testid="kanzlei-form">
      <div>
        <h3 className="font-serif text-2xl">Kanzleidaten</h3>
        <p className="text-sm text-[#4B5563] mt-1">Diese Daten erscheinen im Briefkopf und Footer Ihrer Schriftsätze.</p>
      </div>
      <div>
        <p className="overline text-[#9CA3AF] mb-3">Anschrift</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-4">
            <Label className="overline">Kanzleiname</Label>
            <Input value={form.kanzlei_name} onChange={e => set("kanzlei_name", e.target.value)} data-testid="kanzlei-kanzlei_name" />
          </div>
          <div className="md:col-span-4"><KanzleiField form={form} set={set} k="kanzlei_adresse" label="Straße & Hausnummer" placeholder="Musterstraße 1" /></div>
          <KanzleiField form={form} set={set} k="kanzlei_plz" label="PLZ" placeholder="10115" />
          <div className="md:col-span-3"><KanzleiField form={form} set={set} k="kanzlei_ort" label="Ort" placeholder="Berlin" /></div>
        </div>
      </div>
      <div>
        <p className="overline text-[#9CA3AF] mb-3">Kontakt</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KanzleiField form={form} set={set} k="kanzlei_telefon" label="Telefon" placeholder="+49 30 123456" />
          <KanzleiField form={form} set={set} k="kanzlei_fax" label="Fax" placeholder="+49 30 123457" />
          <KanzleiField form={form} set={set} k="kanzlei_email" label="E-Mail" placeholder="kanzlei@example.de" />
          <KanzleiField form={form} set={set} k="kanzlei_website" label="Website" placeholder="https://kanzlei.de" />
        </div>
      </div>
      <div>
        <p className="overline text-[#9CA3AF] mb-3">Berufsangaben (Pflicht)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KanzleiField form={form} set={set} k="kanzlei_kammer" label="Anwaltskammer" placeholder="Rechtsanwaltskammer Berlin" />
          <KanzleiField form={form} set={set} k="kanzlei_gerichtsstand" label="Gerichtsstand" placeholder="Berlin" />
          <KanzleiField form={form} set={set} k="kanzlei_ustid" label="USt-IdNr." placeholder="DE123456789" />
        </div>
      </div>
      <AktenzeichenSection form={form} set={set} />
      <div>
        <p className="overline text-[#9CA3AF] mb-3">Bankverbindung — Geschäftskonto</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KanzleiField form={form} set={set} k="kanzlei_bank" label="Bank" placeholder="Sparkasse Berlin" />
          <KanzleiField form={form} set={set} k="kanzlei_bic" label="BIC" placeholder="BELADEBEXXX" />
          <div className="md:col-span-2"><KanzleiField form={form} set={set} k="kanzlei_iban" label="IBAN Geschäftskonto" placeholder="DE89 3704 0044 0532 0130 00" /></div>
        </div>
      </div>
      <div>
        <p className="overline text-[#9CA3AF] mb-3">Anderkonto (für Schadenersatz-Zahlungen der Versicherung)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><KanzleiField form={form} set={set} k="kanzlei_anderkonto_iban" label="IBAN Anderkonto" placeholder="DE95 3707 0024 0226 0826 00" /></div>
          <KanzleiField form={form} set={set} k="kanzlei_anderkonto_bic" label="BIC Anderkonto" placeholder="DEUTDEDBKOE" />
        </div>
      </div>
      <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="kanzlei-save">
        {saving ? "Speichert..." : "Kanzleidaten speichern"}
      </Button>
      </form>
    </div>
  );
}

function SupportForm() {
  const [form, setForm] = useState({ subject: "", message: "", kategorie: "frage" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support", form);
      setSent(true);
      setForm({ subject: "", message: "", kategorie: "frage" });
      toast.success("Support-Anfrage gesendet");
    } catch {
      toast.error("Fehler beim Senden");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={send} className="bg-white border border-[#E5E7EB] rounded-lg p-8 space-y-4" data-testid="support-form">
        <div>
          <h3 className="font-serif text-2xl">Support kontaktieren</h3>
          <p className="text-sm text-[#4B5563] mt-1">Wir antworten werktags innerhalb von 24 Stunden.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="overline">Kategorie</Label>
            <Select value={form.kategorie} onValueChange={v => setForm(f => ({ ...f, kategorie: v }))}>
              <SelectTrigger data-testid="support-kategorie"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="frage">Allgemeine Frage</SelectItem>
                <SelectItem value="fehler">Fehler / Bug</SelectItem>
                <SelectItem value="feature">Feature-Wunsch</SelectItem>
                <SelectItem value="sonstige">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Betreff</Label>
            <Input required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} data-testid="support-subject" />
          </div>
        </div>
        <div>
          <Label className="overline">Nachricht</Label>
          <Textarea required rows={6} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} data-testid="support-message" placeholder="Beschreiben Sie Ihr Anliegen..." />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={sending} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="support-send">
            <Envelope size={16} className="mr-2" /> {sending ? "Sendet..." : "Nachricht senden"}
          </Button>
          {sent && <span className="text-sm text-[#16A34A] flex items-center gap-1"><CheckCircle size={14} /> Gesendet</span>}
        </div>
      </form>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8">
        <h3 className="font-serif text-2xl mb-4">Direkte Kontaktwege</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <p className="overline text-[#9CA3AF] mb-1">E-Mail</p>
            <p className="font-mono">support@lexauto.de</p>
          </div>
          <div>
            <p className="overline text-[#9CA3AF] mb-1">Telefon</p>
            <p className="font-mono">+49 30 12345678</p>
          </div>
          <div>
            <p className="overline text-[#9CA3AF] mb-1">Reaktionszeit</p>
            <p>werktags &lt; 24 h</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function HilfeSection() {
  const faqs = [
    ["Wie importiere ich ein Gutachten?", "Öffnen Sie die Akte und ziehen Sie die PDF-Datei per Drag & Drop in den Dokumente-Tab. Alle relevanten Daten werden automatisch extrahiert."],
    ["Welches Modell wird für die automatischen Schriftsätze verwendet?", "Claude Sonnet 4.5 (Anthropic), DSGVO-konform über Server in der EU."],
    ["Wie werden RVG-Gebühren berechnet?", "Automatisch nach §13 RVG (Tabelle Anlage 2) mit Faktor 1,3 als Geschäftsgebühr + 20 € Auslagenpauschale + 19% USt. Die Bezifferung erscheint direkt in der Akte."],
    ["Wie rechne ich den Nutzungsausfall?", "Nach Tabelle Sanden/Danner/Küppersbusch (Fahrzeuggruppen A–L). Die passende Gruppe wird automatisch aus dem Gutachten erkannt, manuell änderbar im Bezifferung-Tab."],
    ["Kann ich Schriftsätze bearbeiten?", "Ja, jeder generierte Schriftsatz kann kopiert und außerhalb weiter bearbeitet werden. Direktes Inline-Editing folgt in einem der nächsten Releases."],
    ["Sind meine Daten sicher?", "Ja — Hosting in Deutschland, Verschlüsselung in Transit und at Rest, DSGVO-konform, automatische Backups."],
  ];
  return (
    <div className="space-y-3" data-testid="hilfe-section">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8">
        <h3 className="font-serif text-2xl mb-2">Häufige Fragen</h3>
        <p className="text-sm text-[#4B5563]">Antworten auf die wichtigsten Fragen zur Nutzung von LexAuto.</p>
      </div>
      {faqs.map(([q, a]) => (
        <details key={q} className="bg-white border border-[#E5E7EB] rounded-lg p-6 group">
          <summary className="cursor-pointer font-serif text-lg flex justify-between items-center">
            <span>{q}</span>
            <span className="text-[#9CA3AF] group-open:rotate-45 transition-transform">+</span>
          </summary>
          <p className="mt-4 text-[#4B5563] leading-relaxed">{a}</p>
        </details>
      ))}
    </div>
  );
}


function EmailServerForm({ user, setUser }) {
  const [form, setForm] = useState({
    imap_host: user?.imap_host || "",
    imap_port: user?.imap_port || 993,
    imap_user: user?.imap_user || "",
    imap_password: user?.imap_password || "",
    imap_ssl: user?.imap_ssl !== false,
    imap_folder: user?.imap_folder || "INBOX",
    imap_auto_sync: user?.imap_auto_sync === true,
    imap_sync_interval_min: user?.imap_sync_interval_min || 5,
    smtp_host: user?.smtp_host || "",
    smtp_port: user?.smtp_port || 587,
    smtp_user: user?.smtp_user || "",
    smtp_password: user?.smtp_password || "",
    smtp_tls: user?.smtp_tls !== false,
    smtp_from_name: user?.smtp_from_name || user?.kanzlei_name || "",
    smtp_from_email: user?.smtp_from_email || user?.kanzlei_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [imapTesting, setImapTesting] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [imapStatus, setImapStatus] = useState(null);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // IMAP-Status (letzter Sync, Auto-Sync) anzeigen
  useEffect(() => {
    api.get("/imap/status").then(r => setSyncStatus(r.data)).catch(() => {});
  }, [user]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", form);
      const updated = { ...user, ...r.data };
      localStorage.setItem("kvr_user", JSON.stringify(updated));
      setUser(updated);
      toast.success("E-Mail-Einstellungen gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  const testImap = async () => {
    setImapTesting(true);
    setImapStatus(null);
    try {
      const r = await api.post("/imap/test", form);
      setImapStatus(r.data);
      if (r.data.ok) toast.success("IMAP-Verbindung OK");
      else toast.error(`IMAP-Fehler: ${r.data.error || ""}`);
    } catch (e) {
      setImapStatus({ ok: false, error: e?.response?.data?.detail });
      toast.error("IMAP-Test fehlgeschlagen");
    } finally { setImapTesting(false); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    setSmtpStatus(null);
    try {
      const r = await api.post("/smtp/test", form);
      setSmtpStatus(r.data);
      if (r.data.ok) toast.success("SMTP-Verbindung OK");
      else toast.error(`SMTP-Fehler: ${r.data.error || ""}`);
    } catch (e) {
      setSmtpStatus({ ok: false, error: e?.response?.data?.detail });
      toast.error("SMTP-Test fehlgeschlagen");
    } finally { setSmtpTesting(false); }
  };

  return (
    <form onSubmit={save} className="space-y-8" data-testid="email-form">
      <div>
        <h2 className="font-serif text-2xl mb-1">E-Mail-Konten</h2>
        <p className="text-sm text-[#6B7280]">Hinterlegen Sie Ihre Kanzlei-Mail-Server-Daten — eingehende Post wird automatisch in den Eingang importiert, ausgehende Schreiben (z.B. Vollmachten) verschickt.</p>
      </div>

      {/* IMAP */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="overline text-[#D32F2F]">IMAP — Posteingang</p>
            <h3 className="font-serif text-xl mt-1">Eingehende E-Mails</h3>
            <p className="text-xs text-[#6B7280] mt-1">PDF-Anhänge werden automatisch in den Eingang importiert.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={testImap} disabled={imapTesting} data-testid="imap-test-btn">
              {imapTesting ? "Teste..." : "Verbindung testen"}
            </Button>
          </div>
        </div>
        {imapStatus && (
          <div className={`text-xs p-2 rounded ${imapStatus.ok ? "bg-[#F0FDF4] text-[#15803D]" : "bg-[#FEF2F2] text-[#B91C1C]"}`} data-testid="imap-status">
            {imapStatus.ok ? "✓ Verbindung erfolgreich" : `✗ ${imapStatus.error}`}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="overline">Host</Label>
            <Input value={form.imap_host} onChange={e => set("imap_host", e.target.value)} placeholder="imap.strato.de" data-testid="imap-host" />
          </div>
          <div>
            <Label className="overline">Port</Label>
            <Input type="number" value={form.imap_port} onChange={e => set("imap_port", parseInt(e.target.value, 10) || 993)} placeholder="993" />
          </div>
          <div>
            <Label className="overline">Benutzer</Label>
            <Input value={form.imap_user} onChange={e => set("imap_user", e.target.value)} placeholder="kanzlei@example.de" data-testid="imap-user" />
          </div>
          <div>
            <Label className="overline">Passwort</Label>
            <Input type="password" value={form.imap_password} onChange={e => set("imap_password", e.target.value)} placeholder="••••••••" data-testid="imap-password" />
          </div>
          <div>
            <Label className="overline">Ordner</Label>
            <Input value={form.imap_folder} onChange={e => set("imap_folder", e.target.value)} placeholder="INBOX" />
          </div>
          <div className="flex items-end gap-2">
            <input id="imap_ssl" type="checkbox" checked={form.imap_ssl} onChange={e => set("imap_ssl", e.target.checked)} className="h-4 w-4" />
            <Label htmlFor="imap_ssl" className="text-sm cursor-pointer mb-0">SSL/TLS</Label>
          </div>
        </div>

        {/* Auto-Sync */}
        <div className="mt-4 pt-4 border-t border-[#E5E7EB] space-y-3">
          <div className="flex items-center gap-2">
            <input
              id="imap_auto_sync"
              type="checkbox"
              checked={form.imap_auto_sync}
              onChange={e => set("imap_auto_sync", e.target.checked)}
              className="h-4 w-4"
              data-testid="imap-auto-sync-toggle"
            />
            <Label htmlFor="imap_auto_sync" className="text-sm cursor-pointer mb-0 font-medium">
              Automatischer Abruf aktivieren
            </Label>
          </div>
          {form.imap_auto_sync && (
            <div className="flex items-center gap-3 pl-6">
              <Label className="text-xs text-[#6B7280] mb-0">Intervall:</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={form.imap_sync_interval_min}
                onChange={e => set("imap_sync_interval_min", Math.max(1, parseInt(e.target.value, 10) || 5))}
                className="w-20"
                data-testid="imap-sync-interval"
              />
              <span className="text-xs text-[#6B7280]">Minuten</span>
            </div>
          )}
          {syncStatus?.last_sync_at && (
            <p className="text-xs text-[#6B7280] pl-6" data-testid="imap-last-sync">
              Letzter Sync: {new Date(syncStatus.last_sync_at).toLocaleString("de-DE")}
            </p>
          )}
        </div>
      </div>

      {/* SMTP */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="overline text-[#D32F2F]">SMTP — Postausgang</p>
            <h3 className="font-serif text-xl mt-1">Ausgehende E-Mails</h3>
            <p className="text-xs text-[#6B7280] mt-1">Zum Versand von Vollmachten und Schriftsätzen direkt aus der App.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={testSmtp} disabled={smtpTesting} data-testid="smtp-test-btn">
              {smtpTesting ? "Teste..." : "Verbindung testen"}
            </Button>
          </div>
        </div>
        {smtpStatus && (
          <div className={`text-xs p-2 rounded ${smtpStatus.ok ? "bg-[#F0FDF4] text-[#15803D]" : "bg-[#FEF2F2] text-[#B91C1C]"}`} data-testid="smtp-status">
            {smtpStatus.ok ? "✓ Verbindung erfolgreich" : `✗ ${smtpStatus.error}`}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="overline">Host</Label>
            <Input value={form.smtp_host} onChange={e => set("smtp_host", e.target.value)} placeholder="smtp.strato.de" data-testid="smtp-host" />
          </div>
          <div>
            <Label className="overline">Port</Label>
            <Input type="number" value={form.smtp_port} onChange={e => set("smtp_port", parseInt(e.target.value, 10) || 587)} placeholder="587" />
          </div>
          <div>
            <Label className="overline">Benutzer</Label>
            <Input value={form.smtp_user} onChange={e => set("smtp_user", e.target.value)} placeholder="kanzlei@example.de" data-testid="smtp-user" />
          </div>
          <div>
            <Label className="overline">Passwort</Label>
            <Input type="password" value={form.smtp_password} onChange={e => set("smtp_password", e.target.value)} placeholder="••••••••" data-testid="smtp-password" />
          </div>
          <div className="flex items-end gap-2">
            <input id="smtp_tls" type="checkbox" checked={form.smtp_tls} onChange={e => set("smtp_tls", e.target.checked)} className="h-4 w-4" />
            <Label htmlFor="smtp_tls" className="text-sm cursor-pointer mb-0">STARTTLS</Label>
          </div>
          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Absender-Name</Label>
              <Input value={form.smtp_from_name} onChange={e => set("smtp_from_name", e.target.value)} placeholder="Kanzlei Mustermann" />
            </div>
            <div>
              <Label className="overline">Absender-E-Mail</Label>
              <Input type="email" value={form.smtp_from_email} onChange={e => set("smtp_from_email", e.target.value)} placeholder="kanzlei@example.de" />
            </div>
          </div>
        </div>
      </div>

      <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="email-save">
        {saving ? "Speichert..." : "Speichern"}
      </Button>
    </form>
  );
}


const DEFAULT_VOLLMACHT_TEXT =
  "in Sachen — wegen Verkehrsunfallschaden — wird hiermit Vollmacht erteilt zur Prozessführung (insbesondere nach §§ 81 ff. ZPO), zur außergerichtlichen Vertretung sowie zur Entgegennahme von Geldern, Wertsachen und Urkunden, insbesondere des Streitgegenstandes und der vom Gegner, von der Justizkasse oder von sonstigen Stellen zu erstattenden Beträge.\n\n" +
  "Die Vollmacht erstreckt sich auf alle bei der Akte anfallenden Tätigkeiten in allen Rechtszügen und schließt die Befugnis zur Erteilung von Untervollmachten ein.";

const DEFAULT_ABTRETUNG_TEXT =
  "Ich, {mandant}, trete hiermit meine Schadensersatzansprüche aus dem Verkehrsunfall vom {unfalldatum} gegen den Unfallverursacher und dessen Kraftfahrzeug-Haftpflichtversicherung in Höhe der mir entstehenden Rechtsanwaltskosten (Geschäftsgebühr nebst Auslagenpauschale und Umsatzsteuer) erfüllungshalber an die Kanzlei {kanzlei} ab.\n\n" +
  "Die Kanzlei ist berechtigt, die abgetretenen Ansprüche im eigenen Namen gegenüber der gegnerischen Haftpflichtversicherung geltend zu machen und Zahlungen entgegenzunehmen. Die Abtretung erfolgt erfüllungshalber; meine Zahlungsverpflichtung bleibt bestehen, soweit die Versicherung nicht oder nicht vollständig leistet.\n\n" +
  "Ich versichere, dass die Ansprüche nicht bereits anderweitig abgetreten oder verpfändet sind.";

const SIGN_MODI = [
  { value: "getrennt", label: "Vollmacht + Abtretung getrennt", hint: "Zwei Dokumente, zwei Unterschriften — je ein PDF in der Akte." },
  { value: "kombiniert", label: "Kombiniertes Dokument", hint: "Ein Dokument „Vollmacht & Abtretungserklärung“, eine Unterschrift." },
  { value: "vollmacht", label: "Nur Vollmacht", hint: "Keine Abtretungserklärung im Portal." },
];

function PortalSettingsForm({ user, setUser }) {
  const [form, setForm] = useState({
    portal_sign_modus: user?.portal_sign_modus || "getrennt",
    portal_auto_link: user?.portal_auto_link !== false,
    vollmacht_template: user?.vollmacht_template || "",
    abtretung_template: user?.abtretung_template || "",
    wiedervorlage_mandant_sichtbar: user?.wiedervorlage_mandant_sichtbar === true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", form);
      localStorage.setItem("kvr_user", JSON.stringify(r.data));
      setUser(r.data);
      toast.success("Portal-Einstellungen gespeichert");
    } catch (err) { toast.error(errMsg(err, "Speichern fehlgeschlagen")); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="portal-settings-form">
      <div>
        <h2 className="font-serif text-2xl mb-1">Mandantenportal</h2>
        <p className="text-sm text-[#6B7280]">Ihre Mandanten sehen den Stand ihrer Akte (Ampel), freigegebene Dokumente, können Unterlagen hochladen, Nachrichten senden und Vollmacht/Abtretung digital unterschreiben. Login: persönlicher Link + Nachname.</p>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-[#D32F2F]">Zugang</p>
            <h3 className="font-serif text-xl mt-1">Portal-Link automatisch senden</h3>
            <p className="text-xs text-[#6B7280] mt-1">Sobald eine Akte einen Mandanten mit E-Mail-Adresse hat (auch per Gutachten-Erkennung) und SMTP konfiguriert ist, geht der Zugangslink automatisch raus. Zusätzlich jederzeit manuell in der Akte.</p>
          </div>
          <Switch checked={form.portal_auto_link} onCheckedChange={v => set("portal_auto_link", v)} data-testid="portal-auto-link-switch" />
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap pt-4 border-t border-[#F3F4F6]">
          <div>
            <h3 className="font-serif text-xl">Geplante Wiedervorlagen im Portal zeigen</h3>
            <p className="text-xs text-[#6B7280] mt-1">Automatische Wiedervorlagen (z. B. „Zahlungseingang prüfen“) werden dem Mandanten als „Geplante Schritte“ angezeigt.</p>
          </div>
          <Switch checked={form.wiedervorlage_mandant_sichtbar} onCheckedChange={v => set("wiedervorlage_mandant_sichtbar", v)} data-testid="portal-wv-sichtbar-switch" />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div>
          <p className="overline text-[#D32F2F]">Digitale Unterschrift</p>
          <h3 className="font-serif text-xl mt-1">Was soll der Mandant unterschreiben?</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SIGN_MODI.map(m => (
            <button
              key={m.value}
              type="button"
              onClick={() => set("portal_sign_modus", m.value)}
              className={`text-left rounded-lg border p-4 transition-colors ${form.portal_sign_modus === m.value ? "border-[#1A2238] bg-[#1A2238] text-white" : "border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
              data-testid={`sign-modus-${m.value}`}
            >
              <p className="font-medium text-sm">{m.label}</p>
              <p className={`text-xs mt-1 ${form.portal_sign_modus === m.value ? "text-white/70" : "text-[#6B7280]"}`}>{m.hint}</p>
            </button>
          ))}
        </div>
        <div>
          <Label className="overline">Vollmachtstext (Standard)</Label>
          <Textarea rows={6} value={form.vollmacht_template} onChange={e => set("vollmacht_template", e.target.value)} placeholder={DEFAULT_VOLLMACHT_TEXT} data-testid="portal-vollmacht-template" />
          <p className="text-[11px] text-[#9CA3AF] mt-1">Leer = Standardtext. Pro Mandant kann der Text im Erfassungs-Modus angepasst werden.</p>
        </div>
        {form.portal_sign_modus !== "vollmacht" && (
          <div>
            <Label className="overline">Abtretungserklärung (Standard)</Label>
            <Textarea rows={7} value={form.abtretung_template} onChange={e => set("abtretung_template", e.target.value)} placeholder={DEFAULT_ABTRETUNG_TEXT} data-testid="portal-abtretung-template" />
            <p className="text-[11px] text-[#9CA3AF] mt-1">Platzhalter: {"{mandant}"} {"{kanzlei}"} {"{unfalldatum}"} {"{aktenzeichen}"} {"{datum}"} — Leer = Standardtext (Sicherungsabtretung der Anwaltskosten).</p>
          </div>
        )}
      </div>

      <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="portal-settings-save">
        {saving ? "Speichert..." : "Speichern"}
      </Button>
    </form>
  );
}
